import type { ReactNode } from "react";

import { useQuery } from "@tanstack/react-query";

import { api } from "../lib/api";
import { money } from "../lib/format";
import type { PaidAction } from "../lib/types";
import { Button } from "./primitives";

type ActionKey = "summary" | "deck" | "discussion" | "finetune";

/**
 * What a paid action may spend, in one sentence, from the server's own
 * constant.
 *
 * The summary, the deck and a discussion question each open a model session
 * with a ceiling and charge no credit — so the ceiling is the only bound on
 * them, and a reader who cannot see it is spending blind. The number comes from
 * `/api/options` rather than being written here, because a copy in the client
 * is a second answer to "what does this cost" that would drift from the first.
 */
export function useActionCeiling(action: ActionKey): PaidAction | undefined {
  const options = useQuery({ queryKey: ["options"], queryFn: api.options });
  return options.data?.actions?.[action];
}

/** The sentence, with the amount in the machine's hand and the words in ours. */
export function SpendLine({ cost }: { cost: PaidAction | undefined }) {
  if (!cost) return <>Uses a model call.</>;
  return (
    <>
      Up to <span className="font-mono tabular-nums text-ink">{money(cost.ceiling_usd)}</span>{" "}
      of model spend.{" "}
      {cost.charged_to_credit ? "Charged to your credit." : "Not charged to your credit."}
    </>
  );
}

/**
 * The step between pressing a paid action and paying for it (#124).
 *
 * Inline rather than a modal: it is a decision about the thing the reader is
 * already looking at, and a dialog would take that away from them. It names
 * what will happen and what it may cost; the action is the primary button and
 * Cancel the quiet one beside it.
 */
export function SpendConfirm({
  action,
  title,
  children,
  confirmLabel,
  onConfirm,
  onCancel,
  pending = false,
}: {
  action: ActionKey;
  title: string;
  children?: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  pending?: boolean;
}) {
  const cost = useActionCeiling(action);
  return (
    <div
      role="group"
      aria-label={title}
      className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line bg-surface-2 px-4 py-3"
    >
      <div className="min-w-0 flex-1 text-sm">
        <p className="font-medium text-ink">{title}</p>
        {children && <p className="mt-0.5 text-xs text-ink-2">{children}</p>}
        <p className="mt-0.5 text-xs text-ink-2">
          <SpendLine cost={cost} />
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={onConfirm}
          disabled={pending}
          autoFocus
        >
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}
